/**
 * S-CP10-4 — System-Band Calculation Engine
 *
 * Computes the recommended timing band (lower–upper %) from the four
 * Q10.TIME questions answered at CP10. No composite points are assigned
 * by this engine — the band is used only to:
 *   (a) display the system-recommended range
 *   (b) trigger over-call / under-call interrupts
 *   (c) build the manager SOFT_STOP payload
 */

export type ScoringTier = "strong" | "medium-strong" | "medium" | "medium-weak" | "capped";

export type TimingTriggerEvent =
  | "budget_cycle_end"
  | "contract_license_expiry"
  | "fiscal_year_end"
  | "project_kickoff_dependency"
  | "regulatory_compliance_deadline"
  | "executive_mandate_with_date"
  | "none_of_these";

export type TimingCustomerConfirmed =
  | "signed_written_commitment"
  | "verbal_from_economic_buyer"
  | "verbal_from_champion_only"
  | "rep_inference_only";

export type TimingPaperRunway =
  | "yes_validated_with_procurement"
  | "yes_rep_estimated"
  | "unknown_no_procurement_contact"
  | "no_runway_tight";

export type TimingSlipHistory = "never" | "once" | "twice" | "three_or_more";

export type SoftStopFlag = "none_of_these_trigger" | "no_runway_tight" | "three_or_more_slips";

export interface TimingAnswer {
  q1_trigger_event: TimingTriggerEvent;
  q2_customer_confirmed: TimingCustomerConfirmed;
  q3_paper_runway: TimingPaperRunway;
  q4_slip_history: TimingSlipHistory;
}

export interface CapResult {
  question: string;
  option_value: string;
  cap_percent: number;
}

export interface TimingBand {
  lower: number;
  upper: number;
  label: string;
  soft_stop_flags: SoftStopFlag[];
  has_caps: boolean;
  active_caps: CapResult[];
}

const Q1_TIER: Record<TimingTriggerEvent, ScoringTier> = {
  budget_cycle_end: "strong",
  contract_license_expiry: "strong",
  fiscal_year_end: "strong",
  project_kickoff_dependency: "strong",
  regulatory_compliance_deadline: "strong",
  executive_mandate_with_date: "strong",
  none_of_these: "capped",
};
const Q1_CAP: Partial<Record<TimingTriggerEvent, number>> = { none_of_these: 25 };

const Q2_TIER: Record<TimingCustomerConfirmed, ScoringTier> = {
  signed_written_commitment: "strong",
  verbal_from_economic_buyer: "medium-strong",
  verbal_from_champion_only: "medium-weak",
  rep_inference_only: "capped",
};
const Q2_CAP: Partial<Record<TimingCustomerConfirmed, number>> = { rep_inference_only: 40 };

const Q3_TIER: Record<TimingPaperRunway, ScoringTier> = {
  yes_validated_with_procurement: "strong",
  yes_rep_estimated: "medium",
  unknown_no_procurement_contact: "capped",
  no_runway_tight: "capped",
};
const Q3_CAP: Partial<Record<TimingPaperRunway, number>> = {
  unknown_no_procurement_contact: 50,
  no_runway_tight: 35,
};

const Q4_TIER: Record<TimingSlipHistory, ScoringTier> = {
  never: "strong",
  once: "medium",
  twice: "medium-weak",
  three_or_more: "capped",
};
const Q4_CAP: Partial<Record<TimingSlipHistory, number>> = { three_or_more: 40 };

interface BaseBand {
  lower: number;
  upper: number;
}

function computeBaseBand(answers: TimingAnswer): BaseBand {
  const tiers: ScoringTier[] = [
    Q1_TIER[answers.q1_trigger_event],
    Q2_TIER[answers.q2_customer_confirmed],
    Q3_TIER[answers.q3_paper_runway],
    Q4_TIER[answers.q4_slip_history],
  ];
  const strongCount = tiers.filter((t) => t === "strong").length;
  const medStrongCount = tiers.filter((t) => t === "medium-strong").length;
  const medCount = tiers.filter((t) => t === "medium").length;
  const medWeakCount = tiers.filter((t) => t === "medium-weak").length;
  const cappedCount = tiers.filter((t) => t === "capped").length;

  if (cappedCount === 0 && medWeakCount === 0 && medCount === 0 && strongCount + medStrongCount === 4) {
    if (strongCount === 4) return { lower: 90, upper: 95 };
    return { lower: 85, upper: 90 };
  }
  if (cappedCount === 0 && medWeakCount === 0) {
    if (strongCount >= 2) return { lower: 75, upper: 85 };
    return { lower: 70, upper: 80 };
  }
  if (cappedCount === 0 && medWeakCount > 0) {
    if (strongCount >= 2) return { lower: 65, upper: 80 };
    return { lower: 60, upper: 75 };
  }
  if (cappedCount >= 2) return { lower: 45, upper: 70 };
  if (cappedCount === 1) return { lower: 50, upper: 75 };
  return { lower: 50, upper: 70 };
}

function collectCaps(answers: TimingAnswer): CapResult[] {
  const caps: CapResult[] = [];
  const q1 = Q1_CAP[answers.q1_trigger_event];
  if (q1 !== undefined) caps.push({ question: "Q10.TIME.1", option_value: answers.q1_trigger_event, cap_percent: q1 });
  const q2 = Q2_CAP[answers.q2_customer_confirmed];
  if (q2 !== undefined) caps.push({ question: "Q10.TIME.2", option_value: answers.q2_customer_confirmed, cap_percent: q2 });
  const q3 = Q3_CAP[answers.q3_paper_runway];
  if (q3 !== undefined) caps.push({ question: "Q10.TIME.3", option_value: answers.q3_paper_runway, cap_percent: q3 });
  const q4 = Q4_CAP[answers.q4_slip_history];
  if (q4 !== undefined) caps.push({ question: "Q10.TIME.4", option_value: answers.q4_slip_history, cap_percent: q4 });
  return caps;
}

function collectSoftStopFlags(answers: TimingAnswer): SoftStopFlag[] {
  const flags: SoftStopFlag[] = [];
  if (answers.q1_trigger_event === "none_of_these") flags.push("none_of_these_trigger");
  if (answers.q3_paper_runway === "no_runway_tight") flags.push("no_runway_tight");
  if (answers.q4_slip_history === "three_or_more") flags.push("three_or_more_slips");
  return flags;
}

export function computeTimingBand(answers: TimingAnswer): TimingBand {
  const base = computeBaseBand(answers);
  const caps = collectCaps(answers);
  const flags = collectSoftStopFlags(answers);

  let upper = base.upper;
  let lower = base.lower;

  if (caps.length > 0) {
    const lowestCap = Math.min(...caps.map((c) => c.cap_percent));
    upper = Math.min(upper, lowestCap);
    lower = Math.min(lower, Math.max(0, upper - 20));
  }
  if (lower >= upper) lower = Math.max(0, upper - 10);

  lower = Math.round(lower / 5) * 5;
  upper = Math.round(upper / 5) * 5;
  if (lower === upper && upper > 0) lower = upper - 5;

  return {
    lower,
    upper,
    label: `Recommended: ${lower}–${upper}%`,
    soft_stop_flags: flags,
    has_caps: caps.length > 0,
    active_caps: caps,
  };
}

export type DeclaredTimingButton = "90" | "80" | "70" | "60" | "50" | "below_50";

export function buttonToPct(button: DeclaredTimingButton): number {
  if (button === "below_50") return 49;
  return parseInt(button, 10);
}

export type OverUnderCallResult =
  | { type: "within_band" }
  | { type: "over_call"; declared: number; band_upper: number; gap: number }
  | { type: "under_call"; declared: number; band_lower: number; gap: number };

export function detectOverUnderCall(button: DeclaredTimingButton, band: TimingBand): OverUnderCallResult {
  const declared = buttonToPct(button);
  if (declared > band.upper) return { type: "over_call", declared, band_upper: band.upper, gap: declared - band.upper };
  if (declared < band.lower) return { type: "under_call", declared, band_lower: band.lower, gap: band.lower - declared };
  return { type: "within_band" };
}

export const SOFT_STOP_FLAG_LABEL: Record<SoftStopFlag, string> = {
  none_of_these_trigger: "No forcing event identified for this close date",
  no_runway_tight: "Runway is tight — procurement timeline risk",
  three_or_more_slips: "This close date has slipped three or more times",
};
